import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { NotificationBlacklistApi } from "./notification-blacklist-api";
import { useMemo } from "react";
import { AssetCameraKeySchema, SiteIdCodec, TenantIdCodec } from "@visioni/a3s-schema/codecs";

const BLACKLIST_QUERY_KEY = ["notification-blacklist"] as const;

export interface UseNotificationBlacklistOptions {
  blacklistApi: NotificationBlacklistApi;
  tenantId: string | undefined;
  enabled?: boolean;
}

/**
 * Parses a scope string (`camera:{tenantId}/{siteId}/{assetCameraKey}`) into its components.
 */
function parseScope(scope: string): { tenantId: string; siteId: string; assetCameraKey: string } | null {
  const prefix = "camera:";
  if (!scope.startsWith(prefix)) return null;

  const path = scope.slice(prefix.length);
  const [tenantIdRaw, siteIdRaw, ...assetCameraKeyParts] = path.split("/");
  const assetCameraKeyRaw = assetCameraKeyParts.join("/");
  if (!tenantIdRaw || !siteIdRaw || !assetCameraKeyRaw) return null;

  const tenantId = TenantIdCodec.safeParse(tenantIdRaw);
  const siteId = SiteIdCodec.safeParse(siteIdRaw);
  const assetCameraKey = AssetCameraKeySchema.safeParse(assetCameraKeyRaw);
  if (!tenantId.success || !siteId.success || !assetCameraKey.success) return null;

  return {
    tenantId: tenantId.data,
    siteId: siteId.data,
    assetCameraKey: assetCameraKey.data,
  };
}

export function useNotificationBlacklist({ blacklistApi, tenantId, enabled = true }: UseNotificationBlacklistOptions) {
  const queryClient = useQueryClient();

  const queryKey = [...BLACKLIST_QUERY_KEY, tenantId ?? "all"];

  const listQuery = useQuery({
    queryKey,
    queryFn: () => blacklistApi.list(tenantId),
    enabled,
  });

  const blacklistedCameraKeys = useMemo(
    () =>
      new Set(
        (listQuery.data?.items ?? [])
          .map((item) => parseScope(item.scope))
          .filter((parsed) => parsed !== null)
          .map((parsed) => parsed.assetCameraKey),
      ),
    [listQuery.data?.items],
  );

  const addMutation = useMutation({
    mutationFn: (params: { tenantId: string; siteId: string; assetCameraKey: string }) =>
      blacklistApi.add(params.tenantId, params.siteId, params.assetCameraKey),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (params: { tenantId: string; siteId: string; assetCameraKey: string }) =>
      blacklistApi.remove(params.tenantId, params.siteId, params.assetCameraKey),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    blacklistedCameraKeys,
    isLoading: listQuery.isLoading,
    addToBlacklist: addMutation.mutate,
    removeFromBlacklist: removeMutation.mutate,
    isMutating: addMutation.isPending || removeMutation.isPending,
  };
}
